/**
 * Copyright © 2024 Delicious AI, LLC
 *
 * @author Stockton Jenkins <stockton.jenkins@deliciousai.com>
 */

import { useQuery } from '@apollo/client'
import {
  GET_LOGGED_IN_USER,
  GET_LOGGED_IN_USER_ULTRA,
  GetLoggedInUserQuery,
} from '@dai/graphql'
import { useEffect } from 'react'
import { Auth } from 'firebase/auth'

const loggedInUserQueryByEndpoint = (
  endPoint: 'gnarnia' | 'mlUltra' | 'mercury',
) => (endPoint === 'mlUltra' ? GET_LOGGED_IN_USER_ULTRA : GET_LOGGED_IN_USER)

export const useLoggedInUser = (
  endPoint: 'gnarnia' | 'mlUltra' | 'mercury' | null,
) => {
  const { data, loading, error, refetch } = useQuery<GetLoggedInUserQuery, {}>(
    loggedInUserQueryByEndpoint(endPoint ?? 'gnarnia'),
    {
      skip: endPoint === null,
      context: { endPoint },
      fetchPolicy: 'network-only',
      notifyOnNetworkStatusChange: true,
    },
  )

  const handleLogout = async (auth: Auth) => {
    try {
      await auth.signOut()
      window.location.replace('/login')
    } catch (error: any) {
      console.error('Error logging out', error)
    }
  }

  return {
    state: {
      loading,
      user: data?.loggedInUser || null,
      error: error?.message || null,
    },
    handle: { handleLogout, handleRefetch: refetch },
  }
}

export const useAuthListener = (
  auth: Auth | null,
  handleRefetch: () => void,
) => {
  useEffect(() => {
    if (!auth) return
    const unsubscribe = auth.onAuthStateChanged(() => {
      handleRefetch()
    })
    return () => {
      unsubscribe()
    }
  }, [])
}
